import { AuditedEntity } from '../../audited-entity'
import { IsoDateStr, IsoIntervalStr, Quantity } from '@elderbyte/ngx-starter'
import { WorkItemWorkOrderAggregate } from '../work-items/aggregations/work-item-work-order-aggregate'
import { OrderConfirmationState, ProdOrderPriority } from '../prod-orders/prod-order'
import { StorageNodeDetails } from '../../storage/nodes/storage-node'
import { ProcessLeadTimeSpec } from '../../process/process-plans/process-plan'

export interface JsonMap<T> {
  [name: string]: T
}

/**
 * implicitly extends WorkOrderHeaderData
 */
export class WorkOrder extends AuditedEntity {
  public id: string

  public company: string

  public prodOrderId: string

  /**
   * facility id
   */
  public manufactureFacility: string

  /**
   * Timezone of facility
   */
  public facilityZone: string

  public collectingFacility: string

  public groupId: string

  public projectId: string
  public projectDescription: string
  public projectSearchTerm: string

  public description: string

  public orderConfirmationState: OrderConfirmationState
  public clearance: WorkClearance

  public labels: string[] = []

  public deliveryInterval: IsoIntervalStr

  public dispatch: WorkOrderDispatch

  /**
   * Contains the max lead time of the contained items and how much in advance they can be released.
   */
  public leadTimeSpec: ProcessLeadTimeSpec

  /**
   * The point in time where this work order must be completed.
   * This is driven the delivery date and the expected manufacturing time frame of the order.
   */
  public dueAt: IsoDateStr

  /**
   * The point in time from which this order can be released to the shop floor.
   */
  public releaseAt: IsoDateStr

  /**
   * The point in time where manufacturing scheduling starts.
   */
  public scheduleInterval: IsoIntervalStr

  public leadTimeMeta?: LeadTimeMeta

  public disableAutoScheduling: boolean

  /**
   * States general high level priority of order.
   */
  public orderPriority: ProdOrderPriority

  public properties: JsonMap<string> = {}

  public note: string

  public isEmpty: boolean

  public numOfPos: number

  public itemsWeight: Quantity

  public completionState: OrderCompletionState

  constructor() {
    super()
  }
}

export class WorkOrderDispatch {
  public deliveryId: string

  public deliveryDispatchPriority: string
}

export class NewWorkOrder {
  constructor(
    public prodOrderId: string,
    public manufactureFacility: string,
    public collectingFacility: string
  ) {}
}

export interface WorkOrderOverview {
  readonly orderId: string
  readonly aggregate: WorkItemWorkOrderAggregate
  readonly itemCount: number
}

export interface WorkOrderStorageLocationOverview {
  readonly workOrderId: string
  readonly baseLocation: StorageNodeDetails
  readonly currentPackageLocation: string
  readonly expectedPackageWeight: Quantity
}

export enum OrderCompletionState {
  NONE = 'NONE',

  OPEN = 'OPEN',

  PARTIAL = 'PARTIAL',

  COMPLETED = 'COMPLETED',
}

export enum DisplayOrderCompletionState {
  OPEN = OrderCompletionState.OPEN,
  PARTIAL = OrderCompletionState.PARTIAL,
  COMPLETED = OrderCompletionState.COMPLETED,
}

export enum WorkClearance {
  /**
   * Can not (yet) be produced.
   */
  HOLD = 'HOLD',

  /**
   * Can be produced (released to the shopfloor).
   * releaseAt date must be respected however.
   */
  RELEASABLE = 'RELEASABLE',
}

export class LeadTimeMeta {
  leadTimeDays: number
  scheduleSkipDays: number
  searchFailed: boolean
  fallBackDays: number
  involvedSchedules: InvolvedSchedule[]
}

export class InvolvedSchedule {
  id: string
  facilityId: string
  name: string
}
